import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { SvgComponent } from '@components/svg.component';
import { CourseCardComponent } from '@components/course-card/course-card.component';
import { SummaryCourseCardComponent } from '@components/summary-course-card/summary-course-card.component';
import { MealPlanModel } from '@models/meal-plan/meal-plan.model';
import { MealPlanPartModel } from '@models/meal-plan/meal-plan-part.model';
import { MenuService } from '@services/menu.service';
import { MealPlanService } from '@services/meal-plan.service';
import { OrderService } from '@services/order.service';
import { CourseModel } from '@models/course/course.model';
import { MenuComponentModel } from '@models/menu/menu-component.model';
import { MenuModel } from '@models/menu/menu.model';
import { OrderCartItemModel } from '@models/order/order-cart.model';
import { ServiceImageService } from '@services/service-image.service';
import { FormatPricePipe } from '@pipes/format-price.pipe';
import { AppRoutes } from '@helpers/app-routes';
import { CompanyService } from '@services/company.service';
import { MealmomentService } from '@services/meal-moment.service';
import { LoaderComponent } from '@components/app-loader/app-loader.component';

interface ServiceCourses {
  part: MealPlanPartModel;
  courses: CourseModel[];
  bgColor?: string | null;
  imageUrl?: string | null;
}

interface SelectedComponent {
  course: CourseModel;
  part: MealPlanPartModel;
  serviceIndex: number;
  bgColor: string | null;
  imageUrl: string | null;
}

@Component({
  selector: 'app-order-page',
  imports: [
    TranslateModule,
    SvgComponent,
    CourseCardComponent,
    SummaryCourseCardComponent,
    FormatPricePipe,
    LoaderComponent,
  ],
  templateUrl: './order-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex h-full bg-white' },
})
export class OrderPageComponent {
  private readonly router = inject(Router);
  private readonly mealPlanService = inject(MealPlanService);
  private readonly orderService = inject(OrderService);
  private readonly menuService = inject(MenuService);
  private readonly toastr = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly serviceImageService = inject(ServiceImageService);
  private readonly companyService = inject(CompanyService);
  private readonly mealmomentService = inject(MealmomentService);

  readonly mealPlan = computed(() => this.mealPlanService.currentMealPlan());
  readonly isMealPlanMode = computed(() => this.mealPlan() !== null);
  readonly isFullMenu = computed(() => this.mealPlan()?.is_full_menu === true);
  readonly searchQuery = signal('');
  readonly isPageLoading = signal(true);
  readonly serviceCourses = signal<ServiceCourses[]>([]);
  readonly quantities = signal<Map<number, number>>(new Map());
  readonly activeServiceId = signal<number | null>(null);
  readonly company = computed(() => this.companyService.currentCompany());

  readonly totalCoursesCount = computed(() =>
    this.serviceCourses().reduce((sum, sc) => sum + sc.courses.length, 0),
  );

  readonly activeService = computed(() => {
    const id = this.activeServiceId();
    if (id === null) return null;
    return this.serviceCourses().find((sc) => sc.part.service_id === id) ?? null;
  });

  readonly filteredServiceCourses = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const activeId = this.activeServiceId();

    let list = this.serviceCourses();

    if (activeId !== null) {
      list = list.filter((sc) => sc.part.service_id === activeId);
    }

    if (!query) return list;

    return list
      .map((sc) => ({
        ...sc,
        courses: sc.courses.filter((c) => c.name.toLowerCase().includes(query)),
      }))
      .filter((sc) => sc.courses.length > 0);
  });

  readonly selectedItems = computed<SelectedComponent[]>(() => {
    const quantities = this.quantities();
    const allServices = this.serviceCourses();
    const result: SelectedComponent[] = [];
    for (let i = 0; i < allServices.length; i++) {
      const sc = allServices[i];
      for (const course of sc.courses) {
        if (quantities.has(course.id)) {
          result.push({
            course,
            part: sc.part,
            serviceIndex: i,
            bgColor: sc.bgColor ?? null,
            imageUrl: sc.imageUrl ?? null,
          });
        }
      }
    }
    return result;
  });

  readonly formulaPrice = computed(() => this.mealPlan()?.final_price ?? 0);

  readonly supplementTotal = computed(() => {
    const quantities = this.quantities();
    const items = this.isMealPlanMode()
      ? this.selectedItems().filter((item) => item.part.service_is_not_in_admission)
      : this.selectedItems();
    return items.reduce(
      (sum, item) => sum + (item.course.price ?? 0) * (quantities.get(item.course.id) ?? 0),
      0,
    );
  });

  readonly totalPrice = computed(() => this.formulaPrice() + this.supplementTotal());

  readonly canValidate = computed(() => {
    const quantities = this.quantities();
    if (quantities.size === 0) return false;
    if (!this.isMealPlanMode()) return true;
    const plan = this.mealPlan()!;
    const requiredParts = plan.admission_parts.filter((part) => part.is_mandatory);
    for (const part of requiredParts) {
      const sc = this.serviceCourses().find((sc) => sc.part.service_id === part.service_id);
      const total = sc?.courses.reduce((sum, c) => sum + (quantities.get(c.id) ?? 0), 0) ?? 0;
      if (total < part.quantity) return false;
    }
    if (plan.max_required) {
      const totalQuantity = this.selectedItems().reduce(
        (sum, item) => sum + (quantities.get(item.course.id) ?? 0),
        0,
      );
      if (totalQuantity < plan.admission_max) return false;
    }
    return true;
  });

  #initialized = false;

  constructor() {
    effect(() => {
      if (this.#initialized || !this.company()) return;
      this.#initialized = true;

      const plan = this.mealPlan();
      if (plan) {
        this.#initMealPlanMode(plan);
      } else {
        this.#initByCourseMode();
      }
    });
  }

  #restoreQuantities(): Map<number, number> | null {
    const order = this.orderService.currentOrder();
    if (!order || order.items.length === 0) return null;
    return new Map(order.items.map((item) => [item.course.id, item.quantity]));
  }

  #getTodayMenu(menus: MenuModel[]): MenuModel | undefined {
    const currentMealMomentId =
      this.mealPlan()?.meal_moment.qdm_id ?? this.mealmomentService.currentMealMoment()?.qdm_id;
    return menus.find((menu) => menu.mealtime_id === currentMealMomentId);
  }

  #initMealPlanMode(plan: MealPlanModel): void {
    const companyId = this.company()?.id;
    if (!companyId) return;

    const parts = [...plan.admission_parts].sort((a, b) => a.service_sort - b.service_sort);
    this.serviceCourses.set(parts.map((part) => ({ part, courses: [] })));

    this.menuService.getMenus(companyId).subscribe({
      next: (menus) => {
        const menu = this.#getTodayMenu(menus);
        this.serviceCourses.update((list) =>
          list.map((sc) => {
            const courses = (menu?.components ?? [])
              .filter((c: MenuComponentModel) => c.family_id === sc.part.service_id)
              .flatMap((c: MenuComponentModel) => c.courses);
            return { ...sc, courses };
          }),
        );
        this.isPageLoading.set(false);

        const restoredQuantities = this.#restoreQuantities();
        if (restoredQuantities) {
          this.quantities.set(restoredQuantities);
        } else if (plan.is_full_menu) {
          const allCourses = this.serviceCourses().flatMap((sc) => sc.courses);
          this.quantities.set(new Map(allCourses.map((c) => [c.id, 1])));
        }
      },
      error: () => {
        this.isPageLoading.set(false);
        this.toastr.error(
          this.translate.instant('order.errors.load_error_message'),
          this.translate.instant('order.errors.load_error_title'),
        );
      },
    });

    this.serviceImageService.getServiceImages(parts.map((p) => p.service_id)).subscribe({
      next: (images) => {
        const imageMap = new Map(images.map((img) => [img.id, img]));
        this.serviceCourses.update((list) =>
          list.map((sc) => {
            const img = imageMap.get(sc.part.service_id);
            return img ? { ...sc, bgColor: `#${img.bg}`, imageUrl: img.url } : sc;
          }),
        );
      },
    });
  }

  #initByCourseMode(): void {
    const companyId = this.company()?.id;
    if (!companyId) return;

    this.menuService.getMenus(companyId).subscribe({
      next: (menus) => {
        const components: MenuComponentModel[] = this.#getTodayMenu(menus)?.components ?? [];
        this.serviceCourses.set(
          components.map((c, index) => ({
            part: {
              quantity: 0,
              service_id: c.family_id,
              is_mandatory: false,
              service_name: c.name,
              service_sort: index,
              service_is_not_in_admission: true,
              is_legume: false,
            },
            courses: c.courses,
          })),
        );
        this.isPageLoading.set(false);

        const restoredQuantities = this.#restoreQuantities();
        if (restoredQuantities) {
          this.quantities.set(restoredQuantities);
        }

        this.serviceImageService.getServiceImages(components.map((c) => c.family_id)).subscribe({
          next: (images) => {
            const imageMap = new Map(images.map((img) => [img.id, img]));
            this.serviceCourses.update((list) =>
              list.map((sc) => {
                const img = imageMap.get(sc.part.service_id);
                return img ? { ...sc, bgColor: `#${img.bg}`, imageUrl: img.url } : sc;
              }),
            );
          },
        });
      },
      error: () => {
        this.isPageLoading.set(false);
        this.toastr.error(
          this.translate.instant('order.errors.load_error_message'),
          this.translate.instant('order.errors.load_error_title'),
        );
      },
    });
  }

  selectService(serviceId: number | null): void {
    this.activeServiceId.set(serviceId);
  }

  getQuantity(courseId: number): number {
    return this.quantities().get(courseId) ?? 0;
  }

  isServiceFull(part: MealPlanPartModel, courses: CourseModel[]): boolean {
    if (part.quantity === 0) return false;
    const quantities = this.quantities();
    const selected = courses.reduce((sum, c) => sum + (quantities.get(c.id) ?? 0), 0);
    return selected >= part.quantity;
  }

  increment(courseId: number): void {
    this.quantities.update((map) => {
      const next = new Map(map);
      next.set(courseId, (next.get(courseId) ?? 0) + 1);
      return next;
    });
  }

  decrement(courseId: number): void {
    this.quantities.update((map) => {
      const current = map.get(courseId) ?? 0;
      const next = new Map(map);
      if (current <= 1) next.delete(courseId);
      else next.set(courseId, current - 1);
      return next;
    });
  }

  getAllButtons() {
    return [
      {
        id: null,
        name: 'order.view_all',
        isAllButton: true,
        coursesCount: this.totalCoursesCount(),
        bgColor: null,
        imageUrl: null,
      },
      ...this.serviceCourses().map((sc) => ({
        id: sc.part.service_id,
        name: sc.part.service_name,
        isAllButton: false,
        coursesCount: sc.courses.length,
        bgColor: sc.bgColor,
        imageUrl: sc.imageUrl,
      })),
    ];
  }

  validate(): void {
    const quantities = this.quantities();
    const seenIds = new Set<number>();
    const items: OrderCartItemModel[] = this.selectedItems()
      .filter((item) => {
        if (seenIds.has(item.course.id)) return false;
        seenIds.add(item.course.id);
        return true;
      })
      .map((item) => ({
        course: item.course,
        part: item.part,
        bgColor: item.bgColor,
        imageUrl: item.imageUrl,
        quantity: quantities.get(item.course.id) ?? 1,
      }));
    this.orderService.save({
      items,
      formulaPrice: this.formulaPrice(),
      supplementTotal: this.supplementTotal(),
      totalPrice: this.totalPrice(),
      isMealPlanMode: this.isMealPlanMode(),
    });
    void this.router.navigate([AppRoutes.orderSummary]);
  }
}
